import { iframeDataBridge } from '@/iframe/iframeDataBridge';
import { authenticatedFetch, getApiUrl } from '@/utils/authService';
import { log } from '@/utils/vueLogger.js';
import { CacheManager } from '@/utils/cacheManager.js';

const deliveryLocationGroupCache = new CacheManager('VueCache', 'cache', {
	maxAge: 24 * 60 * 60 * 1000,
	memoryCacheTTL: 30 * 60 * 1000,
	logCategory: 'DeliveryLocationGroup',
	keyPrefix: () => `${iframeDataBridge.getCustomer() ?? 'unknown'}.${iframeDataBridge.getCurrentUser()?.id ?? 'unknown'}.deliveryLocationGroups.`,
});

export async function getDeliveryLocationGroups(forceRefresh = false) {
	if (!forceRefresh) {
		const memCached = deliveryLocationGroupCache.getFromMemory('all');
		if (memCached) return memCached;

		try {
			const legacyData = await iframeDataBridge.getStorageAsync('DELIVERYLOCATIONSGROUP');
			if (legacyData) {
				log('info', 'DeliveryLocationGroup', '📦 Seeding Vue cache from legacy storage (DELIVERYLOCATIONSGROUP)');
				await deliveryLocationGroupCache.put('all', legacyData);
				deliveryLocationGroupCache.setInMemory('all', legacyData);
				return legacyData;
			}
		} catch (legacyError) {
			log('warn', 'DeliveryLocationGroup', `⚠️ Legacy storage check failed: ${legacyError.message}`);
		}
	}

	try {
		const cached = await deliveryLocationGroupCache.get('all');
		if (cached && !forceRefresh) {
			deliveryLocationGroupCache.setInMemory('all', cached);

			if (!iframeDataBridge.isOnline()) {
				log('info', 'DeliveryLocationGroup', '📴 Offline: Returning cached delivery location groups');
				return cached;
			}

			setTimeout(() => refreshDeliveryLocationGroupsInBackground(), 100);
			return cached;
		}

		if (!iframeDataBridge.isOnline()) {
			log('warn', 'DeliveryLocationGroup', '📴 Offline: No cached delivery location groups available');
			return [];
		}

		return await fetchDeliveryLocationGroupsFromAPI();
	} catch (error) {
		log('error', 'DeliveryLocationGroup', `❌ Error getting delivery location groups: ${error.message}`);

		try {
			const fallback = await deliveryLocationGroupCache.get('all');
			if (fallback) {
				log('info', 'DeliveryLocationGroup', '💾 Using fallback cached delivery location groups');
				return fallback;
			}
		} catch (cacheError) {
			log('error', 'DeliveryLocationGroup', `❌ Cache fallback failed: ${cacheError.message}`);
		}

		return [];
	}
}

async function fetchDeliveryLocationGroupsFromAPI() {
	try {
		const response = await authenticatedFetch(getApiUrl('Sales/DeliveryLocationGroups'), { method: 'GET' }, 'tokenSales');

		if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

		const deliveryLocationGroups = await response.json();
		await deliveryLocationGroupCache.put('all', deliveryLocationGroups);
		deliveryLocationGroupCache.setInMemory('all', deliveryLocationGroups);

		log('success', 'DeliveryLocationGroup', `✅ Fetched and cached ${deliveryLocationGroups.length} delivery location groups`);
		return deliveryLocationGroups;
	} catch (error) {
		log('error', 'DeliveryLocationGroup', `❌ Failed to fetch delivery location groups from API: ${error.message}`);
		throw error;
	}
}

async function refreshDeliveryLocationGroupsInBackground() {
	try {
		await fetchDeliveryLocationGroupsFromAPI();
		log('success', 'DeliveryLocationGroup', '🔄 Background refresh of delivery location groups completed');
	} catch (error) {
		log('warn', 'DeliveryLocationGroup', `⚠️ Background refresh failed: ${error.message}`);
	}
}

export async function refreshDeliveryLocationGroups() {
	deliveryLocationGroupCache.clearMemory();
	await deliveryLocationGroupCache.clear();
}
